import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Breadcrumbs from '../../Common/Breadcrumbs';
import SummaryCards from './components/SummaryCards';
import FilterSection from './components/FilterSection';
import PaymentsTable from './components/PaymentsTable';
import InvoiceModal from './components/InvoiceModal';
import { 
  getAllTransactions, 
  PaymentTransaction,
  PaymentStats
} from '../../api/superAdminPayments/paymentsService';
import { getSuperAdminDashboardStats } from '../../api/dashboard/dashboardService';
import { hasAnyPermission, hasPermission, type User } from '../../utils/roleBasedAccess';
import './SuperAdminPayments.css';

const SuperAdminPayments: React.FC = () => {
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<PaymentTransaction[]>([]); // Store all transactions for filter options
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PaymentStats>({
    totalRevenue: 0,
    realizedProfit: 0,
    paymentDues: 0
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter state
  const [filters, setFilters] = useState<{
    ageCategory: string;
    status: string;
    teams: string;
    tournaments: string;
  }>({
    ageCategory: 'All',
    status: 'All',
    teams: 'All',
    tournaments: 'All'
  });
  
  // Modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransaction | null>(null);

  // Check if user has access
  const userData: User = {
    ...(user || {}),
    subRole: role,
    permissions: permissions,
  };

  const isAdminUser = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'tournament.payments.view',
  ]);

  useEffect(() => {
    if (!isAdminUser) {
      setLoading(false);
      setError('Access denied. You do not have permission to view this page.');
      return;
    }
    loadData();
  }, [isAdminUser]);

  // Handle page changes with client-side filtering
  useEffect(() => {
    if (!isInitialLoad && allTransactions.length > 0) {
      // Apply client-side filtering and pagination
      const filteredTransactions = applyClientSideFilters(allTransactions);
      const startIndex = (currentPage - 1) * 10;
      const endIndex = startIndex + 10;
      const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
      
      setTransactions(paginatedTransactions);
    }
  }, [currentPage, allTransactions, filters]);

  // Apply client-side filtering when filters change
  useEffect(() => {
    if (allTransactions.length > 0) {
      // Apply client-side filtering
      const filteredTransactions = applyClientSideFilters(allTransactions);
      
      // Update pagination based on filtered results
      setTotalCount(filteredTransactions.length);
      setTotalPages(Math.ceil(filteredTransactions.length / 10));
      
      // Reset to page 1 and get transactions for current page
      setCurrentPage(1);
      const startIndex = 0;
      const endIndex = 10;
      const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
      
      setTransactions(paginatedTransactions);
    }
  }, [filters, allTransactions]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAllTransactions(), // Load all transactions
        loadPaymentStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load payment data. Please try again.');
    } finally {
      setLoading(false);
      setIsInitialLoad(false); // Mark initial load as complete
    }
  };

  const loadPaymentStats = async () => {
    try {
      const dashboardStats = await getSuperAdminDashboardStats();
      
      // Map dashboard stats to payment stats format
      const paymentStats: PaymentStats = {
        totalRevenue: dashboardStats.totalAmount || dashboardStats.payments || 0,
        realizedProfit: dashboardStats.totalAmount || dashboardStats.payments || 0, // Using total amount as realized profit
        paymentDues: dashboardStats.totalDues || 0
      };
      
      setStats(paymentStats);
    } catch (error) {
      console.error('Error loading payment stats:', error);
      // Keep existing stats if dashboard API fails
    }
  };

  const loadAllTransactions = async () => {
    try {
      setIsPageLoading(true);
      setError(null);

      let allTransactionsData: PaymentTransaction[] = [];
      let currentPage = 1;
      let hasMoreData = true;

      // Load all pages of data
      while (hasMoreData) {
        const response = await getAllTransactions(currentPage, {});
        
        if (response && response.data) {
          const pageTransactions = response.data.paginatedResults || [];
          allTransactionsData = [...allTransactionsData, ...pageTransactions];
          
          // Check if there are more pages
          const totalCount = response.data.totalCount || 0;
          const totalPages = Math.ceil(totalCount / 10);
          hasMoreData = currentPage < totalPages;
          currentPage++;
        } else {
          hasMoreData = false;
        }
      }
      
      // Store all transactions for both filtering and display
      setAllTransactions(allTransactionsData);
      
      // Apply client-side filtering to get filtered transactions
      const filteredTransactions = applyClientSideFilters(allTransactionsData);
      
      // Update pagination based on filtered results
      setTotalCount(filteredTransactions.length);
      setTotalPages(Math.ceil(filteredTransactions.length / 10));
      
      // Get transactions for first page
      const startIndex = 0;
      const endIndex = 10;
      const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
      
      setTransactions(paginatedTransactions);
      setCurrentPage(1);
      
    } catch (error) {
      console.error('Error loading transactions:', error);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setIsPageLoading(false);
    }
  };

  // Client-side filtering function
  const applyClientSideFilters = (transactions: PaymentTransaction[]): PaymentTransaction[] => {
    return transactions.filter(transaction => {
      // Age Category filter
      if (filters.ageCategory !== 'All') {
        const ageCategory = transaction.teamInfo?.ageCategory || transaction.tournamentInfo?.ageCategory;
        if (!ageCategory || ageCategory !== filters.ageCategory) {
          return false;
        }
      }

      // Status filter
      if (filters.status !== 'All') {
        if (transaction.paidStatus !== filters.status) {
          return false;
        }
      }

      // Teams filter
      if (filters.teams !== 'All') {
        if (!transaction.teamInfo?.name || transaction.teamInfo.name !== filters.teams) {
          return false;
        }
      }

      // Tournaments filter
      if (filters.tournaments !== 'All') {
        if (!transaction.tournamentInfo?.name || transaction.tournamentInfo.name !== filters.tournaments) {
          return false;
        }
      }

      return true;
    });
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleViewTransaction = (transaction: PaymentTransaction) => {
    setSelectedTransaction(transaction);
    setShowInvoiceModal(true);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedTransaction(null);
  };

  if (!isAdminUser) {
    return (
      <div className="super-admin-payments-page">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ph-warning" style={{ fontSize: '64px', color: '#dc3545' }}></i>
            <h3 className="mt-3 text-danger">Access Denied</h3>
            <p className="text-muted">You do not have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="super-admin-payments-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Tournaments',
                active: false
              },
              {
                label: 'Payments Listing',
                active: true
              }
            ]} 
          />
        </div>

        {/* Page Header */}
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <h2 className="mb-2 fw-bold" style={{ color: '#212529' }}>
            Payments & Transactions
          </h2>
          <p className="text-muted mb-0">
            Manage and view all payment transactions across tournaments
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{ padding: '0 15px' }}>
          <SummaryCards stats={stats} />
        </div>

        {/* Filter Section */}
        <div style={{ padding: '0 15px' }}>
          <FilterSection 
            filters={filters}
            onFilterChange={handleFilterChange}
            transactions={allTransactions} // Use all transactions for filter options
          />
        </div>

        {/* Payments Table */}
        <div style={{ padding: '0 15px' }}>
          <PaymentsTable
            transactions={transactions}
            loading={loading || isPageLoading}
            onViewTransaction={handleViewTransaction}
            showActions={true} // Show Actions column for super-admin and operations-admin
          />
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-section mt-4" style={{ 
            padding: '16px 15px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Page Info */}
            <div className="page-info mb-3">
              <span className="text-muted" style={{ 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#6c757d'
              }}>
                Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} transactions
              </span>
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls d-flex align-items-center justify-content-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="me-2"
              >
                Previous
              </Button>
              
              <div className="page-numbers d-flex align-items-center">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="mx-1"
                      style={{ minWidth: '40px' }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ms-2"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Invoice Modal */}
                <InvoiceModal 
          show={showInvoiceModal}
          onHide={handleCloseInvoiceModal}
          transaction={selectedTransaction}
          generatePDF={true}
        />
      </Container>
    </div>
  );
};

export default SuperAdminPayments; 